import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import AlertsPanel from './AlertsPanel';
import QuickCreateFAB from './QuickCreateFAB';
import ImpersonationBanner from './ImpersonationBanner';
import { useBrowserNotifications } from '../hooks/useBrowserNotifications';
import { useUserStatus } from '../hooks/useUserStatus';
import { useRealtimeNotifications } from '../hooks/useRealtimeNotifications';

function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { appliedTheme } = useTheme();
  const isDark = appliedTheme === 'dark';
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile menu
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // Default collapsed on desktop
  const [sidebarHovered, setSidebarHovered] = useState(false); // Track hover state
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [alertStats, setAlertStats] = useState({ total_unread: 0 });
  const [statusNotifications, setStatusNotifications] = useState([]);
  const notificationRef = useRef(null);
  const alertsRef = useRef(null);

  // Browser notifications & user presence
  const { permission, requestPermission, checkNewNotifications, unreadCount, soundEnabled, toggleSound } = useBrowserNotifications();
  const { myStatus, teamStatuses, showPicker, setShowPicker, updateStatus, STATUS_CONFIG } = useUserStatus();

  // Sidebar is expanded when hovered OR when mobile menu is open
  const isSidebarExpanded = sidebarHovered || sidebarOpen;

  // Handle sidebar hover
  const handleSidebarMouseEnter = () => {
    setSidebarHovered(true);
  };

  const handleSidebarMouseLeave = () => {
    setSidebarHovered(false);
  };

  // Fetch notifications (tasks + email activity + status change notifications)
  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      // Fetch tasks (replaces reminders)
      let urgentTasks = [];
      try {
        const tasksResponse = await axios.get(`${API}/tasks`);
        const tasks = tasksResponse.data || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        urgentTasks = tasks.filter(t => {
          if (t.status === 'done') return false;
          if (!t.due_date) return false;
          const taskDate = new Date(t.due_date);
          taskDate.setHours(0, 0, 0, 0);
          return taskDate <= today;
        }).map(t => ({ ...t, notificationType: 'task' }));
      } catch (e) {
        // Tasks endpoint might not exist
      }

      // Fetch all notifications (includes email opens, clicks, status changes)
      let allApiNotifications = [];
      try {
        const notifResponse = await axios.get(`${API}/notifications?limit=20`);
        allApiNotifications = notifResponse.data
          .filter(n => !n.read)
          .map(n => ({ ...n, notificationType: n.type || 'status_change' }));
      } catch (e) {
        // Notifications endpoint might not exist for some users
      }

      // Separate email notifications for special display
      const emailNotifications = allApiNotifications.filter(n =>
        n.type === 'email_opened' || n.type === 'email_link_clicked'
      );
      const otherNotifications = allApiNotifications.filter(n =>
        n.type !== 'email_opened' && n.type !== 'email_link_clicked'
      );

      // Fetch alert stats
      try {
        const alertsResponse = await axios.get(`${API}/alerts/stats`);
        setAlertStats(alertsResponse.data);
      } catch (e) {
        // Alerts endpoint might not exist
      }

      // Combine and sort by date - email notifications first, then tasks, then others
      const allNotifications = [
        ...emailNotifications.slice(0, 3),
        ...urgentTasks.slice(0, 3),
        ...otherNotifications.slice(0, 3)
      ]
        .sort((a, b) => new Date(b.created_at || b.due_date) - new Date(a.created_at || a.due_date))
        .slice(0, 8);

      setNotifications(allNotifications);
      setNotificationCount(emailNotifications.length + urgentTasks.length + otherNotifications.length);
      setStatusNotifications(otherNotifications);

      // Browser notification check (triggers popups + tab title)
      checkNewNotifications(token);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [checkNewNotifications]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Real-time WebSocket — any Graph-driven event (mail/calendar/contact) triggers a
  // near-instant re-fetch of notifications & alert counts. Polling above remains as fallback.
  useRealtimeNotifications((evt) => {
    if (!evt) return;
    if (evt.type === 'graph_change' || evt.type === 'notification') {
      fetchNotifications();
    }
  });

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setNotificationCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Role hierarchy — mirrors backend ROLE_HIERARCHY in models/enums.py
  // Higher level = more permissions. Any role at or above minLevel sees the item.
  const ROLE_LEVELS = {
    sdr: 1,
    sales_executive: 2,
    pre_sales_engineer: 2,
    team_leader: 3,
    solutions_architect: 3,
    manager: 4,
    regional_sales_head: 5,
    country_sales_head: 6,
    company_sales_head: 7,
    admin: 8,
  };

  const userLevel = ROLE_LEVELS[user?.role] || 0;
  const isPreSales = user?.role === 'pre_sales_engineer' || user?.role === 'solutions_architect';

  // Format role for display: "country_sales_head" -> "Country Sales Head"
  const formatRole = (role) => {
    if (!role) return '';
    return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // Pre-sales roles get a NARROW nav: only the tools they need to execute on
  // assigned requests. They don't browse the whole sales pipeline.
  const presalesNav = [
    { name: 'Pre-Sales', href: '/presales', icon: PreSalesIcon, minLevel: 1 },
    { name: 'Activities', href: '/activities', icon: CalendarIcon, minLevel: 1 },
    { name: 'Pre-Sales Calendar', href: '/presales/calendar', icon: CalendarIcon, minLevel: 1 },
    { type: 'divider', minLevel: 1 },
    { name: 'Products', href: '/products', icon: ProductIcon, minLevel: 1 },
    { name: 'Email', href: '/email', icon: InboxIcon, minLevel: 1 },
    { type: 'divider', minLevel: 1 },
    { type: 'header', label: 'Analytics', minLevel: 1 },
    { name: 'Reports', href: '/reports', icon: DocumentIcon, minLevel: 1 },
    { name: 'Team Stats', href: '/team', icon: TeamIcon, minLevel: 3 }, // SA only
    { type: 'divider', minLevel: 1 },
    { name: 'Settings', href: '/settings', icon: SettingsIcon, minLevel: 1 },
  ];

  const fullNav = [
    // Core CRM — visible to everyone (level 1+)
    { name: 'Dashboard', href: '/', icon: HomeIcon, minLevel: 1 },
    { name: 'Management Hub', href: '/manage', icon: ZapIcon, minLevel: 3 },
    { name: 'Leads', href: '/leads', icon: UsersIcon, minLevel: 1 },
    { name: 'Opportunities', href: '/opportunities', icon: OpportunityIcon, minLevel: 1 },
    { name: 'Accounts', href: '/accounts', icon: AccountsIcon, minLevel: 1 },
    { name: 'Contacts', href: '/contacts', icon: ContactIcon, minLevel: 1 },
    { name: 'Products', href: '/products', icon: ProductIcon, minLevel: 7 },
    // Divider
    { type: 'divider', minLevel: 1 },
    // Communication — visible to everyone
    { name: 'Email', href: '/email', icon: InboxIcon, minLevel: 1 },
    { name: 'Activities', href: '/activities', icon: CalendarIcon, minLevel: 1 },
    { name: 'Communications', href: '/communications', icon: ChatIcon, minLevel: 1 },
    { name: 'Pre-Sales', href: '/presales', icon: PreSalesIcon, minLevel: 1 },
    // Divider
    { type: 'divider', minLevel: 1 },
    // Automation — visible to everyone, Assignment Rules for managers+
    { type: 'header', label: 'Automation', minLevel: 1 },
    { name: 'Sequences', href: '/sequences', icon: SequenceIcon, minLevel: 1 },
    { name: 'Assignment Rules', href: '/assignment-rules', icon: RulesIcon, minLevel: 4 },
    // Divider
    { type: 'divider', minLevel: 1 },
    // Analytics — visible to everyone, Team Stats for team_leader+
    { type: 'header', label: 'Analytics', minLevel: 1 },
    { name: 'Reports', href: '/reports', icon: DocumentIcon, minLevel: 1 },
    { name: 'Team Stats', href: '/team', icon: TeamIcon, minLevel: 3 },
    { name: 'Performance', href: '/dashboard', icon: ChartBarIcon, minLevel: 1 },
    // Divider
    { type: 'divider', minLevel: 1 },
    // Tools — visible to everyone (SDR self-imports are scoped to their own leads by the backend)
    { name: 'Import/Export', href: '/import-export', icon: ImportIcon, minLevel: 1 },
    // Divider
    { type: 'divider', minLevel: 4 },
    // Admin — manager+ for Team Management, admin-only for Security
    { type: 'header', label: 'Admin', minLevel: 4 },
    { name: 'Team Management', href: '/team-management', icon: CogIcon, minLevel: 4 },
    { name: 'Security', href: '/security', icon: ShieldIcon, minLevel: 8 },
    { name: 'Audit Log', href: '/admin/audit', icon: ShieldIcon, minLevel: 7 },
    { name: 'Settings', href: '/settings', icon: SettingsIcon, minLevel: 1 },
  ];

  // Pre-sales roles get the narrow nav; everyone else gets the full nav
  const navigation = isPreSales ? presalesNav : fullNav;

  const filteredNav = navigation.filter(item => userLevel >= item.minLevel);

  return (
    <div className="min-h-screen flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Collapsed by default, expands on hover */}
      <aside 
        className={`
          fixed inset-y-0 left-0 z-50
          transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          md:translate-x-0 transition-all duration-300 ease-in-out
          ${isSidebarExpanded ? 'w-64' : 'w-20'} bg-slate-900 flex flex-col h-screen
        `}
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
      >
        {/* Logo - Fixed at top */}
        <div className={`h-16 flex items-center ${!isSidebarExpanded ? 'justify-center px-2' : 'px-4'} border-b border-slate-800 flex-shrink-0 transition-all duration-300`}>
          <div className="flex items-center min-w-0">
            <img 
              src={isDark ? "/logo-red-tight.png" : "/logo-white-tight.png"} 
              alt="intalk.io" 
              className={`${!isSidebarExpanded ? 'h-5 w-auto' : 'h-6 w-auto max-w-[180px]'} object-contain transition-all duration-300`}
            />
          </div>
          {/* Mobile close button */}
          {isSidebarExpanded && (
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden ml-auto p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Navigation - Scrollable with hidden scrollbar */}
        <nav className={`flex-1 ${!isSidebarExpanded ? 'px-2' : 'px-3'} py-4 space-y-1 overflow-y-auto scrollbar-hide transition-all duration-300`}>
          {filteredNav.map((item, index) => {
            // Render divider
            if (item.type === 'divider') {
              return (
                <div key={`divider-${item.label || index}`} className="my-3 border-t border-slate-800" />
              );
            }
            
            // Render section header
            if (item.type === 'header') {
              if (!isSidebarExpanded) return null;
              return (
                <div key={`header-${item.label}`} className="pt-2 pb-1 px-3">
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    {item.label}
                  </span>
                </div>
              );
            }
            
            // Render nav item
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`nav-item ${isActive ? 'nav-item-active' : 'nav-item-inactive'} ${!isSidebarExpanded ? 'justify-center px-2' : ''} group relative`}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                title={!isSidebarExpanded ? item.name : ''}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                {isSidebarExpanded && <span className="truncate font-body">{item.name}</span>}
                {/* Tooltip for collapsed state */}
                {!isSidebarExpanded && (
                  <span className="absolute left-full ml-3 px-2 py-1 bg-slate-800 text-white text-sm rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-lg">
                    {item.name}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User section at bottom - Fixed */}
        <div className={`${!isSidebarExpanded ? 'p-2' : 'p-4'} border-t border-slate-800 flex-shrink-0 transition-all duration-300`}>
          <div className={`flex items-center ${!isSidebarExpanded ? 'justify-center' : 'gap-3'} group relative`}>
            {/* Avatar with status dot */}
            <div className="relative flex-shrink-0">
              {user?.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.name}
                  className={`${!isSidebarExpanded ? 'w-10 h-10' : 'w-10 h-10'} rounded-xl object-cover shadow-lg`}
                  data-testid="sidebar-user-avatar"
                />
              ) : (
                <div className={`${!isSidebarExpanded ? 'w-10 h-10' : 'w-10 h-10'} rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              )}
              {/* Status indicator dot */}
              <button
                onClick={() => setShowPicker(!showPicker)}
                className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 ${STATUS_CONFIG[myStatus]?.color || 'bg-slate-400'} rounded-full border-2 border-slate-900 cursor-pointer hover:scale-125 transition-transform`}
                title={`Status: ${STATUS_CONFIG[myStatus]?.label || 'Offline'} — Click to change`}
                data-testid="user-status-dot"
              />
            </div>
            {isSidebarExpanded && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 font-body">{STATUS_CONFIG[myStatus]?.label || 'Offline'} &middot; {formatRole(user?.role)}</p>
              </div>
            )}
            {/* Tooltip for collapsed state */}
            {!isSidebarExpanded && (
              <span className="absolute left-full ml-3 px-2 py-1 bg-slate-800 text-white text-sm rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-lg">
                {user?.name}<br/>
                <span className="text-slate-400 text-xs">{STATUS_CONFIG[myStatus]?.label} &middot; {formatRole(user?.role)}</span>
              </span>
            )}
          </div>

          {/* Status picker dropdown */}
          {showPicker && (
            <div className={`${!isSidebarExpanded ? 'absolute left-full bottom-2 ml-3' : 'mt-2'} bg-slate-800 border border-slate-700 rounded-lg shadow-xl overflow-hidden z-50`}
              data-testid="status-picker-dropdown"
            >
              {Object.entries(STATUS_CONFIG).filter(([k]) => k !== 'offline').map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => { updateStatus(key); setShowPicker(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-slate-700 transition-colors ${myStatus === key ? 'bg-slate-700/50' : ''}`}
                  data-testid={`status-option-${key}`}
                >
                  <span className={`w-2.5 h-2.5 rounded-full ${cfg.color}`}></span>
                  <span className="text-slate-200">{cfg.label}</span>
                  {myStatus === key && (
                    <svg className="w-4 h-4 text-emerald-400 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </aside>

      {/* Main content area - Dynamic left margin based on sidebar state */}
      <div className={`flex-1 flex flex-col min-w-0 ${isSidebarExpanded ? 'md:ml-64' : 'md:ml-20'} bg-slate-50 dark:bg-slate-900 transition-all duration-300`}>
        <ImpersonationBanner />
        {/* Top bar - Sticky */}
        <header className="h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          {/* Mobile menu button */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden p-2 -ml-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
            data-testid="mobile-menu-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Page title area */}
          <div className="hidden md:block">
            <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">
              {filteredNav.find(item => item.href === location.pathname && item.name)?.name || 'Dashboard'}
            </h1>
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-2 ml-auto">
            {/* Browser notification permission banner */}
            {permission === 'default' && (
              <button
                onClick={requestPermission}
                className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                data-testid="enable-notifications-btn"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                Enable notifications
              </button>
            )}

            {/* Notification sound toggle */}
            {permission === 'granted' && (
              <button
                onClick={toggleSound}
                className={`p-2 rounded-lg transition-colors ${soundEnabled ? 'text-slate-500 hover:text-slate-700 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700' : 'text-slate-300 dark:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                title={soundEnabled ? 'Sound on — click to mute' : 'Sound off — click to unmute'}
                data-testid="notification-sound-toggle"
              >
                {soundEnabled ? (
                  <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                ) : (
                  <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  </svg>
                )}
              </button>
            )}
            {/* Alerts Dropdown */}
            <div className="relative" ref={alertsRef}>
              <button 
                onClick={() => {
                  setAlertsOpen(!alertsOpen);
                  setNotificationsOpen(false);
                }}
                className="p-2 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg relative"
                data-testid="alerts-btn"
                title="Lead Alerts"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                {alertStats.total_unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-amber-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center">
                    {alertStats.total_unread > 9 ? '9+' : alertStats.total_unread}
                  </span>
                )}
              </button>

              {/* Alerts Dropdown */}
              {alertsOpen && (
                <div className="absolute right-0 mt-2 w-96 z-50">
                  <AlertsPanel />
                </div>
              )}
            </div>

            {/* Notifications Dropdown */}
            <div className="relative" ref={notificationRef}>
              <button 
                onClick={() => {
                  setNotificationsOpen(!notificationsOpen);
                  setAlertsOpen(false);
                }}
                className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg relative"
                data-testid="notifications-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {notificationCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </button>

              {/* Dropdown */}
              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between bg-white dark:bg-slate-800">
                    <h3 className="font-semibold text-slate-800 dark:text-white">Notifications</h3>
                    {notificationCount > 0 && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full">
                        {notificationCount} pending
                      </span>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto bg-white dark:bg-slate-800">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <svg className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-slate-500 dark:text-slate-400 text-sm">All caught up!</p>
                        <p className="text-slate-400 dark:text-slate-500 text-xs mt-1">No pending notifications</p>
                      </div>
                    ) : (
                      notifications.map((notification, idx) => {
                        // Email opened notification
                        if (notification.type === 'email_opened') {
                          return (
                            <Link
                              key={notification.id || idx}
                              to="/email"
                              onClick={() => {
                                markAsRead(notification.id);
                                setNotificationsOpen(false);
                              }}
                              className="block px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 border-b border-slate-50 dark:border-slate-700 last:border-0 bg-green-50/50 dark:bg-green-900/10"
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                                  <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-green-800 dark:text-green-300">
                                    Email Opened!
                                  </p>
                                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 font-medium">
                                    {notification.lead_name || 'Someone'} opened your email
                                  </p>
                                  <p className="text-xs text-slate-500 dark:text-slate-500 mt-0.5 truncate">
                                    "{notification.email_subject || 'No subject'}"
                                  </p>
                                </div>
                              </div>
                            </Link>
                          );
                        }
                        
                        // Email link clicked notification
                        if (notification.type === 'email_link_clicked') {
                          return (
                            <Link
                              key={notification.id || idx}
                              to="/email"
                              onClick={() => {
                                markAsRead(notification.id);
                                setNotificationsOpen(false);
                              }}
                              className="block px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-b border-slate-50 dark:border-slate-700 last:border-0 bg-blue-50/50 dark:bg-blue-900/10"
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                  <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                                  </svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                                    Link Clicked!
                                  </p>
                                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 font-medium">
                                    {notification.lead_name || 'Someone'} clicked a link
                                  </p>
                                  <p className="text-xs text-slate-500 dark:text-slate-500 mt-0.5 truncate">
                                    In: "{notification.email_subject || 'No subject'}"
                                  </p>
                                </div>
                              </div>
                            </Link>
                          );
                        }
                        
                        // Status change notification
                        if (notification.notificationType === 'status_change' || notification.change_type) {
                          return (
                            <div
                              key={notification.id || idx}
                              className="block px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700 border-b border-slate-50 dark:border-slate-700 last:border-0"
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                                  <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                  </svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-slate-800 dark:text-white">
                                    {notification.change_type || notification.title} Changed
                                  </p>
                                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                                    <span className="font-medium">{notification.entity_name || notification.message}</span>
                                  </p>
                                  {notification.old_value && (
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                      <span className="line-through text-red-400">{notification.old_value}</span>
                                      {' → '}
                                      <span className="text-emerald-600 dark:text-emerald-400 font-medium">{notification.new_value}</span>
                                    </p>
                                  )}
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markAsRead(notification.id);
                                  }}
                                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                  title="Mark as read"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          );
                        }
                        
                        // Task notification (was reminder)
                        const isOverdue = notification.due_date && new Date(notification.due_date) < new Date();
                        return (
                          <Link
                            key={idx}
                            to="/tasks"
                            onClick={() => setNotificationsOpen(false)}
                            className="block px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700 border-b border-slate-50 dark:border-slate-700 last:border-0"
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isOverdue ? 'bg-red-100 dark:bg-red-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                                <svg className={`w-4 h-4 ${isOverdue ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-800 dark:text-white truncate">
                                  {notification.title || notification.lead_name || 'Task reminder'}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                                  {notification.description || notification.notes || 'No details'}
                                </p>
                                <p className={`text-xs mt-1 ${isOverdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-amber-600 dark:text-amber-400'}`}>
                                  {isOverdue ? 'Overdue' : 'Due today'}
                                </p>
                              </div>
                            </div>
                          </Link>
                        );
                      })
                    )}
                  </div>
                  <Link
                    to="/tasks"
                    onClick={() => setNotificationsOpen(false)}
                    className="block px-4 py-3 text-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-t border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800"
                  >
                    View all tasks →
                  </Link>
                </div>
              )}
            </div>

            {/* User menu / Logout */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all"
              data-testid="logout-button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="hidden sm:inline text-sm font-medium">Sign out</span>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-3 sm:p-4 md:p-6 overflow-auto bg-slate-50 dark:bg-slate-900">
          {children}
        </main>

        {/* Quick Create FAB */}
        <QuickCreateFAB />
      </div>
    </div>
  );
}

// Icon Components
function HomeIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}

function UsersIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function PlusCircleIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ChartBarIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}

function CogIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function TeamIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function CalendarIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function DocumentIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function PipelineIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  );
}

function ChatIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}

function BellIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}

function ImportIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
    </svg>
  );
}

function SettingsIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
    </svg>
  );
}

function CompanyIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

function ContactIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function ProductIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );
}

function KanbanIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
    </svg>
  );
}

function FollowUpIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

function SequenceIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  );
}

function InboxIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
    </svg>
  );
}

function ShieldIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function ZapIcon({ className }) {
  // Lightning bolt — Management Hub icon (matches the heading emoji)
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

function RulesIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16m-7 6h7" />
    </svg>
  );
}

function OpportunityIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function PreSalesIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function AccountsIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}


export default DashboardLayout;
